import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { DialogLogo } from './DialogLogo';
import { AnimaLogo } from '~/components/figma/AnimaLogo';
import { LegacyButton } from '~/components/ui/LegacyButton';
import { Dialog, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import { ErrorKind, FigmaError, StatusKind, type Status, type Tasks } from '~/lib/figma/useFigmaImport';
import { classNames } from '~/utils/classNames';
import { wait } from '~/utils/promises';

type TaskStatus = Tasks['fetchDesign']['status'];

const ICON_CLASS = 'inline-block align-middle';

interface Props {
  status: Status;
  tasks: Tasks;
  target: string;
  onTryAgain: () => void;
  onDismiss: () => void;
}

const ITEM_CLASS = 'inline-flex gap-2 items-center text-sm';

const INITIAL_DURATION_WARNING = 'This might take a while';
const DURATION_WARNINGS = ['Still working', 'Hang tight', 'Nearly there'];

export function FigmaLoadingScreen({ tasks, target, status, onTryAgain, onDismiss }: Props) {
  const [slowMessageIndex, setSlowMessageIndex] = useState<null | number>(null);

  const codeGenerationStarted = tasks.codeGeneration.status !== 'pending';
  const uploadingStarted = tasks.uploadAssets.status !== 'pending';
  const fetchStatus = tasks.fetchDesign.status === 'pending' ? 'running' : tasks.fetchDesign.status;

  const slowMessage =
    slowMessageIndex === null
      ? null
      : slowMessageIndex >= 0
        ? DURATION_WARNINGS[slowMessageIndex]
        : INITIAL_DURATION_WARNING;

  useEffect(() => {
    if (!codeGenerationStarted || uploadingStarted) {
      setSlowMessageIndex(null);

      return () => {
        /* NOOP */
      };
    }

    let canceled = false;

    async function warningsLoop() {
      await wait(3_000);

      setSlowMessageIndex(-1);

      while (true) {
        await wait(15_000);

        if (canceled) {
          break;
        }

        setSlowMessageIndex((index) => {
          return index === null ? 0 : (index + 1) % DURATION_WARNINGS.length;
        });
      }
    }

    warningsLoop();

    return () => {
      canceled = true;
    };
  }, [codeGenerationStarted, uploadingStarted]);

  const progress = (
    <ul className="mb-28 text-center flex flex-col items-center gap-5">
      <li className={ITEM_CLASS}>
        <StatusIcon className={ICON_CLASS} status={fetchStatus} /> Fetching{' '}
        <code
          className="px-6px py-4px rounded-md select-text bg-bolt-elements-artifacts-inlineCode-background text-bolt-elements-artifacts-inlineCode-text max-w-25em truncate"
          title={target}
        >
          <span className="text-bolt-elements-textSecondary">#</span>
          {target}
        </code>{' '}
        from Figma
      </li>
      <li
        className={classNames(ITEM_CLASS, 'transition-opacity opacity-0 duration-300', {
          'opacity-100': codeGenerationStarted,
        })}
      >
        <StatusIcon className={ICON_CLASS} status={tasks.codeGeneration.status} /> Generating code
      </li>
      <motion.li
        key={`${tasks.uploadAssets.status}:${slowMessage ?? ''}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {uploadingStarted ? (
          <div className={ITEM_CLASS}>
            <StatusIcon className={ICON_CLASS} status={tasks.uploadAssets.status} /> Uploading assets
          </div>
        ) : (
          <span
            className={classNames('text-bolt-elements-textTertiary italic text-sm', {
              invisible: slowMessage == null || !codeGenerationStarted,
            })}
          >
            {slowMessage || <>&nbsp;</>}
          </span>
        )}
      </motion.li>
    </ul>
  );

  const errorMessageElement = (error: Error) => (
    <div className="text-center">
      <h1 className="mb-2 font-semibold text-lg">An error occurred</h1>
      <p className="mb-3em max-w-30em">{errorMessage(error)}</p>
      <LegacyButton onClick={onTryAgain} className="inline-block mb-20" size="lg" variant="primary">
        Try Again
      </LegacyButton>
    </div>
  );

  const error = status.type === StatusKind.Error ? status.error : false;

  return (
    <Dialog
      open={true}
      onClose={onDismiss}
      contentClassName="max-w-170"
      closeOnBackdropClick={false}
      showCloseButton={!!error}
      backdropClassName="bg-bolt-elements-background-depth-1"
    >
      <VisuallyHidden>
        <DialogTitle>Importing Figma Frame</DialogTitle>
      </VisuallyHidden>
      <DialogDescription noPadding>
        <DialogLogo />
        <div className="px-8 pb-6 flex flex-col items-center w-full text-center">
          {!error && progress}

          {!error && (
            <div className="mb-4 text-sm text-bolt-elements-textSecondary">
              Tip: To make the generated code responsive,{' '}
              <a target="_blank" href="https://help.figma.com/hc/articles/5731482952599-Add-auto-layout-to-a-design">
                add auto layout
              </a>{' '}
              to your frame.
            </div>
          )}

          {error && errorMessageElement(error)}

          <div className="text-sm text-bolt-elements-textTertiary">
            Powered by <VisuallyHidden>Anima</VisuallyHidden>{' '}
            <AnimaLogo className="fill-[var(--bolt-elements-textTertiary)]" />
          </div>
        </div>
      </DialogDescription>
    </Dialog>
  );
}

function StatusIcon({ status, className }: { status: TaskStatus; className?: string }) {
  const iconSize = 'text-lg';

  switch (status) {
    case 'pending': {
      return <div className={classNames(className, 'i-ph:circle-duotone text-bolt-elements-textTertiary', iconSize)} />;
    }
    case 'running': {
      return (
        <div
          className={classNames(
            className,
            'i-svg-spinners:90-ring-with-bg text-bolt-elements-loader-progress',
            iconSize,
          )}
        />
      );
    }
    case 'finished': {
      return <div className={classNames(className, 'i-ph:check text-bolt-elements-icon-success', iconSize)} />;
    }
  }
}

function errorMessage(error: Error) {
  const payload = error instanceof FigmaError ? error.payload : undefined;

  switch (payload?.kind) {
    case ErrorKind.Auth: {
      return 'Unable to connect to Figma. Please try again.';
    }
    case ErrorKind.InvalidNode: {
      return 'The selected node in the Figma document is not supported. Please try again with a link to a frame.';
    }
    case ErrorKind.MissingNode: {
      return 'The Figma link does not specify a node. Please try with a different frame link.';
    }
    case ErrorKind.CodegenBackend: {
      return 'There was an unexpected error during code generation. Please try again or choose a different frame.';
    }
    case ErrorKind.RateLimit: {
      return 'You have hit a rate limit. Please upgrade to import designs from Figma.';
    }
    case ErrorKind.AssetUploadSize: {
      return 'The generated assets for this frame are too large. Please choose a different frame.';
    }
    case ErrorKind.AssetUpload: {
      return 'There was an unexpected error while uploading assets. Please try again or choose a different frame.';
    }
    case ErrorKind.FigmaFile: {
      if (payload.status === 404) {
        return 'There was an error trying to access the Figma frame. Please ensure that you can access the frame or choose a different one.';
      }

      return 'There was an unexpected error while retrieving the Figma frame. Please try again or choose a different one.';
    }
    case ErrorKind.NoResult:
    default: {
      return 'There was an unexpected error. Please try again or choose a different frame.';
    }
  }
}
